/**
 * src/hooks/useStreamingTTS.js — Streaming TTS audio playback queue
 *
 * Fetches TTS audio from /api/ai?action=agent.tts_stream and queues it for
 * sequential playback via Web Audio API.
 *
 * TICKET-WISPR-TTS-STREAM (2026-07-05) — true streaming playback. The server
 * has always streamed TTS bytes (chunked transfer); the old client buffered
 * the ENTIRE sentence (`await resp.arrayBuffer()`) then decoded it in one
 * shot, so first audio paid full-synth + full-download + full-decode
 * serially. Now the client requests raw PCM (`format:'pcm'`) and plays chunks
 * AS THEY ARRIVE through src/lib/voice/pcmPlayer.js — Wispr-Flow-style
 * perceived latency. Content negotiation is by response Content-Type:
 *   - `audio/pcm`   → streaming path (pcmPlayer, first audio ≈ first chunk)
 *   - anything else → legacy buffered path (decodeAudioData) — covers older
 *     servers and non-PCM fallback providers (e.g. Gemini TTS), so a provider
 *     fallback mid-outage degrades latency, never audio.
 * Kill switch: localStorage acn_tts_transport = 'buffered' forces the legacy
 * path (no `format` hint sent) if streaming ever misbehaves on a device.
 *
 * iOS Safari compatibility: AudioContext must be created/resumed inside a
 * user gesture handler. The PTTButton press event serves as the unlock gesture.
 * Call unlockAudio() from a click/touchstart handler before the first speak().
 *
 * Phase 90-05 — watches AudioContext.state and drains the queue when iOS
 * suspends/interrupts the context (phone call, Siri). The session-level
 * monitor in useVoiceConversation owns the full cleanup; this is a local
 * safety net so the queue doesn't keep decoding into a dead graph.
 *
 * Usage:
 *   const { speak, stop, isSpeaking, unlockAudio } = useStreamingTTS();
 *   unlockAudio(); // call once inside a user gesture handler
 *   speak('Hello, here is your update.');
 *   stop();        // cancel current audio + clear queue
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { getAuthHeaders } from '../lib/api/client';
import { startTimer, logVoiceEvent } from '../lib/voice/voiceLogger';
import { splitWords, globalWordIndex } from '../lib/voice/karaoke';
import { createPcmPlayer } from '../lib/voice/pcmPlayer';

const TTS_API = '/api/ai';
const TTS_FETCH_TIMEOUT_MS = 25_000;
const KARAOKE_TICK_MS = 60;

/** 'buffered' forces the legacy full-buffer path; anything else = streaming. */
function getTransportPreference() {
  if (typeof localStorage === 'undefined') return 'stream';
  try {
    return localStorage.getItem('acn_tts_transport') === 'buffered' ? 'buffered' : 'stream';
  } catch {
    // spine-emit-ok: localStorage denied (private mode) — default transport is fine
    return 'stream';
  }
}

/** Parse `rate=NNN` out of a Content-Type like `audio/pcm;rate=24000`. */
export function parsePcmRate(contentType, fallback = 24000) {
  const m = /rate=(\d{4,6})/i.exec(contentType || '');
  return m ? Number(m[1]) : fallback;
}

export default function useStreamingTTS({ onAudioInterrupted, onError, isSpeakAllowed, onFirstAudio, onWordBoundary } = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  // isPending: true while TTS fetch(es) are in flight (audio not yet fully received)
  const [isPending, setIsPending] = useState(false);

  const audioCtxRef = useRef(null);
  // 2026-05-01 (Voice quick win #2) — fires ONCE per playback streak, the
  // moment the first audio starts. Caller (useVoiceConversation) wires this
  // to a turnLatency tracker's markFirstAudio() so we can measure
  // user-perceived "time-to-first-audio" against the recorded baseline. Reset
  // by stop() so a new turn re-arms the mark.
  const onFirstAudioRef = useRef(onFirstAudio);
  onFirstAudioRef.current = onFirstAudio;
  const firstAudioFiredRef = useRef(false);
  // TICKET-073 (karaoke) — per-word highlight timer. onWordBoundary fires with
  // { seq, wordIndex, globalWordIndex, wordCount } as each word is "spoken",
  // and with null on stop()/barge-in so the UI clears the highlight. On the
  // buffered path pacing is audioBuffer.duration / wordCount; on the streaming
  // path it's paced against pcmPlayer.scheduledSeconds() (grows as chunks
  // arrive — synth outruns speech, so it converges within the first words).
  const onWordBoundaryRef = useRef(onWordBoundary);
  onWordBoundaryRef.current = onWordBoundary;
  const seqTextRef = useRef(new Map());   // seq:number → spoken text
  const wordTimerRef = useRef(null);      // setInterval id for the per-word advance
  // 2026-04-20 (session 5) — seq-keyed Map so playback honors EMISSION order
  // (sentenceBuffer call order), not fetch COMPLETION order.
  // TICKET-WISPR-TTS-STREAM — values are now queue ITEMS, not bare buffers:
  //   { kind:'pcm', chunks:Uint8Array[], done:boolean, rate:number,
  //     player:object|null, bytes:number }   — streaming; player attaches when
  //                                            the seq reaches head-of-queue
  //   { kind:'encoded', arrayBuffer }        — legacy buffered MP3/whatever
  const queueRef = useRef(new Map());
  const emitSeqRef = useRef(0);          // next seq to assign (speak side)
  const playSeqRef = useRef(0);          // next seq to play (playback side)
  // Set of seqs whose fetch failed or was skipped — playNext advances past
  // them so the queue doesn't stall on a permanently empty slot.
  const skippedSeqsRef = useRef(new Set());
  const playingRef = useRef(false);
  const currentSourceRef = useRef(null);   // buffered path's live source
  const activePcmPlayerRef = useRef(null); // streaming path's live player
  const cancelledRef = useRef(false);
  const pendingCountRef = useRef(0);
  const statechangeHandlerRef = useRef(null);
  const onAudioInterruptedRef = useRef(onAudioInterrupted);
  onAudioInterruptedRef.current = onAudioInterrupted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // F3 (2026-04-21) — caller-supplied gate. Predicate is read
  // synchronously at the top of speak(); returning false refuses to
  // enqueue and skips the seq. Caller keeps the mode state elsewhere
  // (e.g. useAgentMode) so this hook stays pure. Backward compatible —
  // when unset, behaves like the pre-F3 hook.
  const isSpeakAllowedRef = useRef(isSpeakAllowed);
  isSpeakAllowedRef.current = isSpeakAllowed;
  // Track every in-flight fetch controller so stop() (user hang-up) can
  // abort immediately instead of leaving an orphan request running the full
  // 25s timeout. Reason='user_cancel' lets the catch block stay silent.
  const inflightControllersRef = useRef(new Set());

  /** Clear the karaoke per-word timer (idempotent). */
  const clearWordTimer = useCallback(() => {
    if (wordTimerRef.current) {
      clearInterval(wordTimerRef.current);
      wordTimerRef.current = null;
    }
  }, []);

  /** Stop current playback and clear queue */
  const stop = useCallback(() => {
    cancelledRef.current = true;
    // TICKET-073 (karaoke) — kill the word timer FIRST so no tick races the
    // teardown, drop the streak's seq→text store, and clear the highlight.
    clearWordTimer();
    seqTextRef.current.clear();
    try { onWordBoundaryRef.current?.(null); } catch { /* observer must not break stop */ }
    queueRef.current.clear();
    skippedSeqsRef.current.clear();
    emitSeqRef.current = 0;
    playSeqRef.current = 0;
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch { /* already stopped */ }
      currentSourceRef.current = null;
    }
    if (activePcmPlayerRef.current) {
      activePcmPlayerRef.current.stop();
      activePcmPlayerRef.current = null;
    }
    // Abort every in-flight TTS fetch. The speak() catch discriminates on
    // signal.reason, so 'user_cancel' stays silent (no onError, no log spam).
    for (const controller of inflightControllersRef.current) {
      try { controller.abort('user_cancel'); } catch { /* noop */ }
    }
    inflightControllersRef.current.clear();
    pendingCountRef.current = 0;
    playingRef.current = false;
    // 2026-05-01 (Voice quick win #2) — re-arm the first-audio mark so the
    // next playback streak fires onFirstAudio exactly once.
    firstAudioFiredRef.current = false;
    setIsSpeaking(false);
    setIsPending(false);
    // Re-enable after clearing
    cancelledRef.current = false;
  }, [clearWordTimer]);

  /** Attach a one-time statechange listener to the AudioContext (Phase 90-05) */
  const attachStatechange = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || typeof ctx.addEventListener !== 'function') return;
    if (statechangeHandlerRef.current) return; // already attached
    const handler = () => {
      const s = ctx.state;
      if (s === 'interrupted' || s === 'suspended') {
        stop();
        onAudioInterruptedRef.current?.(s);
      }
    };
    ctx.addEventListener('statechange', handler);
    statechangeHandlerRef.current = handler;
  }, [stop]);

  /** Create or resume AudioContext — must be called from a user gesture */
  const unlockAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (err) {
        console.error('[useStreamingTTS] AudioContext creation failed:', err.message);
        return;
      }
    }
    if (audioCtxRef.current.state === 'suspended') {
      // 2026-04-20 Phase 3 (audit P1-2): was `.catch(() => {})` — a silent
      // swallow that hid "tap to enable audio" flakiness. Failure here means
      // the browser rejected the resume (iOS lockscreen, Safari autoplay
      // policy, missing user gesture). Surface to the caller so the overlay
      // can prompt the user, and log so SQL can show the frequency.
      audioCtxRef.current.resume().catch((err) => {
        logVoiceEvent('tts_audio_resume_failed', 'error',
          `AudioContext.resume rejected: ${err?.message || err}`, {
            state_before: 'suspended',
            state_after: audioCtxRef.current?.state || 'unknown',
            error: String(err?.message || err).slice(0, 200),
          });
        onErrorRef.current?.('audio_suspended', { state: audioCtxRef.current?.state });
      });
    }
    attachStatechange();
  }, [attachStatechange]);

  /**
   * TICKET-073 (karaoke) — streaming-path word pacing. The buffered path knows
   * the clip duration up front and keeps its original fixed-interval pacing
   * (duration / wordCount) inline in playNext. On the streaming path the total
   * duration GROWS as chunks arrive (getTotalSec = player.scheduledSeconds()),
   * so this timer accumulates elapsed wall-clock per tick and emits every word
   * whose fraction of the current total has been reached — monotonic by
   * construction (w only advances), converging to exact pacing as soon as the
   * stream completes (synth outruns speech, typically within the first words).
   * Torn down on onended/stop/unmount so it never outlives the audio
   * (barge-in leak guard). Observer wrapped — must not break audio.
   */
  const startStreamWordPacing = useCallback((seq, getTotalSec) => {
    clearWordTimer();
    if (typeof onWordBoundaryRef.current !== 'function') return;
    const spokenText = seqTextRef.current.get(seq) || '';
    const words = splitWords(spokenText);
    const wordCount = words.length;
    if (wordCount === 0) return;
    const base = globalWordIndex(seqTextRef.current, seq, 0);
    const emitWord = (wordIndex) => {
      try {
        onWordBoundaryRef.current?.({ seq, wordIndex, globalWordIndex: base + wordIndex, wordCount });
      } catch { /* observer must not break playback */ }
    };
    emitWord(0);
    if (wordCount === 1) return;
    let w = 1;
    let elapsedSec = 0;
    wordTimerRef.current = setInterval(() => {
      elapsedSec += KARAOKE_TICK_MS / 1000;
      const totalSec = getTotalSec();
      if (!(totalSec > 0)) return;
      while (w < wordCount && elapsedSec >= (w / wordCount) * totalSec) {
        emitWord(w);
        w += 1;
      }
      if (w >= wordCount) clearWordTimer();
    }, KARAOKE_TICK_MS);
  }, [clearWordTimer]);

  /** Fire the once-per-streak first-audio mark (turn_latency measurement). */
  const fireFirstAudio = useCallback(() => {
    if (!firstAudioFiredRef.current && typeof onFirstAudioRef.current === 'function') {
      firstAudioFiredRef.current = true;
      try { onFirstAudioRef.current(); } catch { /* observer must not break playback */ }
    }
  }, []);

  /** Play next item in the queue */
  const playNext = useCallback(() => {
    if (cancelledRef.current) {
      setIsSpeaking(false);
      playingRef.current = false;
      return;
    }
    // Advance past any seqs marked skipped (their fetches failed) so a gap
    // in the middle of a reply doesn't permanently stall playback.
    while (skippedSeqsRef.current.has(playSeqRef.current)) {
      skippedSeqsRef.current.delete(playSeqRef.current);
      playSeqRef.current++;
    }
    // Only play the NEXT sequential seq — if it hasn't landed yet (its fetch
    // is still in flight), wait. The next speak() call that fills this slot
    // will trigger playNext() again.
    const nextSeq = playSeqRef.current;
    if (!queueRef.current.has(nextSeq)) {
      setIsSpeaking(false);
      playingRef.current = false;
      return;
    }
    const ctx = audioCtxRef.current;
    if (!ctx) {
      setIsSpeaking(false);
      playingRef.current = false;
      return;
    }

    const item = queueRef.current.get(nextSeq);
    queueRef.current.delete(nextSeq);
    playSeqRef.current = nextSeq + 1;
    playingRef.current = true;
    setIsSpeaking(true);

    // ── Streaming PCM path (TICKET-WISPR-TTS-STREAM) ─────────────────────
    // The seq reached head-of-queue: attach a live player, replay whatever
    // chunks buffered while it waited, and let speak()'s read loop append
    // the rest directly. First audio fires on the FIRST scheduled chunk —
    // no full-download wait, no decodeAudioData.
    if (item.kind === 'pcm') {
      const player = createPcmPlayer({
        ctx,
        sampleRate: item.rate,
        onStarted: () => {
          // 2026-06-06 playback instrumentation — emit START with the live
          // AudioContext state (re-checked at play time, not just before
          // fetch) so iOS suspended-at-play roots stay visible. Duration is
          // unknown at start on the streaming path; the 'ok' row carries it.
          logVoiceEvent('tts_playback', 'start', `starting stream (seq=${nextSeq})`, {
            seq: nextSeq,
            ctx_state: ctx.state,
            transport: 'pcm_stream',
          });
          fireFirstAudio();
          startStreamWordPacing(nextSeq, () => player.scheduledSeconds());
        },
        onEnded: () => {
          activePcmPlayerRef.current = null;
          clearWordTimer();
          // "did the CEO hear it" event — playback actually FINISHED.
          logVoiceEvent('tts_playback', 'ok', `playback ended (seq=${nextSeq})`, {
            seq: nextSeq,
            ctx_state: ctx.state,
            audio_duration_ms: Math.round(player.scheduledSeconds() * 1000),
            transport: 'pcm_stream',
          });
          playNext();
        },
      });
      activePcmPlayerRef.current = player;
      item.player = player;
      for (const chunk of item.chunks) player.append(chunk);
      item.chunks = [];
      if (item.done) player.end();
      return;
    }

    // ── Legacy buffered path (encoded audio → decodeAudioData) ───────────
    const buffer = item.arrayBuffer;
    ctx.decodeAudioData(buffer, (audioBuffer) => {
      if (cancelledRef.current) {
        setIsSpeaking(false);
        playingRef.current = false;
        return;
      }
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      currentSourceRef.current = source;
      source.onended = () => {
        currentSourceRef.current = null;
        // TICKET-073 (karaoke) — this clip is done; stop its word timer. The
        // next seq re-arms on its own start(); on the final seq the highlight
        // stays on the last word until the speaking phase ends (no flicker).
        clearWordTimer();
        // 2026-06-06 playback instrumentation — confirm the audio actually
        // FINISHED playing. Without this, a fetched-but-never-heard turn on
        // iOS (AudioContext interrupted, source silently never starts) was
        // invisible past tts_fetch. This is the "did the CEO hear it" event.
        logVoiceEvent('tts_playback', 'ok', `playback ended (seq=${nextSeq})`, {
          seq: nextSeq,
          ctx_state: ctx.state,
        });
        playNext();
      };
      // 2026-06-06 playback instrumentation — emit START with the live
      // AudioContext state. On iOS the context can be 'suspended'/'interrupted'
      // at play time even though the fetch succeeded; capturing ctx.state here
      // (re-checked at play time, not just before fetch) reveals that root.
      logVoiceEvent('tts_playback', 'start', `starting source (seq=${nextSeq})`, {
        seq: nextSeq,
        ctx_state: ctx.state,
        audio_duration_ms: Math.round((audioBuffer?.duration || 0) * 1000),
      });
      source.start(0);
      // 2026-05-01 (Voice quick win #2) — first-audio mark for the
      // user-perceived latency budget. Fires once per playback streak;
      // stop() resets so the next turn re-arms it. Observer is wrapped
      // because logging must never break playback.
      fireFirstAudio();
      // TICKET-073 (karaoke) — drive a per-word highlight across THIS clip's
      // duration. Word 0 fires now (on start); the rest advance on an interval
      // paced by duration/wordCount. Any prior timer is cleared first; this one
      // is torn down on onended/stop/decode-error/unmount so it never outlives
      // the audio (barge-in leak guard). Observer wrapped — must not break audio.
      clearWordTimer();
      if (typeof onWordBoundaryRef.current === 'function') {
        const spokenText = seqTextRef.current.get(nextSeq) || '';
        const words = splitWords(spokenText);
        const wordCount = words.length;
        if (wordCount > 0) {
          const base = globalWordIndex(seqTextRef.current, nextSeq, 0);
          const emitWord = (wordIndex) => {
            try {
              onWordBoundaryRef.current?.({ seq: nextSeq, wordIndex, globalWordIndex: base + wordIndex, wordCount });
            } catch { /* observer must not break playback */ }
          };
          emitWord(0);
          const durationMs = Math.round((audioBuffer?.duration || 0) * 1000);
          if (wordCount > 1 && durationMs > 0) {
            const perWord = Math.max(1, durationMs / wordCount);
            let w = 1;
            wordTimerRef.current = setInterval(() => {
              if (w >= wordCount) { clearWordTimer(); return; }
              emitWord(w);
              w += 1;
            }, perWord);
          }
        }
      }
    }, (err) => {
      // TICKET-073 (karaoke) — decode failed for this seq; ensure no word timer
      // lingers from a prior clip before we skip ahead.
      clearWordTimer();
      const msg = err?.message || String(err);
      console.error('[useStreamingTTS] decodeAudioData failed:', msg);
      logVoiceEvent('tts_decode', 'error', `decodeAudioData failed: ${msg}`);
      // 2026-06-06 playback instrumentation — mirror the decode failure on
      // the tts_playback step so a single /admin/observability filter on
      // step='tts_playback' shows the full start/ok/error lifecycle.
      logVoiceEvent('tts_playback', 'error', `decode failed before playback: ${msg}`, {
        seq: nextSeq,
        ctx_state: ctx.state,
        error_message: msg,
      });
      onErrorRef.current?.('audio_decode_failed', { message: msg });
      playNext(); // skip failed buffer, continue queue
    });
  }, [clearWordTimer, startStreamWordPacing, fireFirstAudio]);

  /**
   * Fetch TTS audio for `text` and enqueue for playback.
   * Returns a promise that resolves when the fetch completes (not when audio ends).
   */
  const speak = useCallback(async (text) => {
    if (!text?.trim()) return;
    if (cancelledRef.current) return;
    // F3 (2026-04-21) — mode gate. If the caller says this mode doesn't
    // permit agent audio (e.g. agentMode !== 'conversation'), refuse to
    // enqueue. Hard-gate prevents the 2026-04-21 dictation-ghost bug
    // even if anything ever re-introduces a background hook chain.
    const allowedFn = isSpeakAllowedRef.current;
    if (typeof allowedFn === 'function' && !allowedFn()) {
      logVoiceEvent('tts_blocked_mode', 'error', 'speak() refused — caller gate returned false', {
        text_preview: text.slice(0, 60),
        text_length: text.length,
        gate_result: 'refused',
      });
      return;
    }
    // F6 — passed the gate. Log WITH a gate-allowed flag so we can
    // cross-reference against mode_transition events in app_error_logs
    // and reconstruct "what mode was active when this audio fired."
    if (typeof allowedFn === 'function') {
      logVoiceEvent('tts_gate_passed', 'ok', 'speak() gate allowed — enqueuing', {
        text_preview: text.slice(0, 60),
        text_length: text.length,
      });
    }
    // Reserve the emission seq NOW, synchronously, so parallel fetches land
    // in the seq-keyed map at the correct slot regardless of completion
    // order. Without this, completion-order would drop or reorder sentences.
    const seq = emitSeqRef.current++;
    // TICKET-073 (karaoke) — remember this seq's text for the whole streak so
    // playback can pace the word highlight and sum earlier seqs' word counts.
    seqTextRef.current.set(seq, text);
    logVoiceEvent('tts_invocation', 'ok', `speak() called: ${text.length} chars (seq=${seq})`, { text_length: text.length, text_preview: text.slice(0, 60), seq });
    const ttsTimer = startTimer('tts_fetch');
    const transport = getTransportPreference();

    // T1.3 — iOS gesture guard: AudioContext MUST be created inside a user
    // gesture via unlockAudio(). No silent fallback creation here.
    if (!audioCtxRef.current) {
      logVoiceEvent('tts_no_context', 'error', 'speak() called without AudioContext — unlockAudio must run first');
      ttsTimer.fail('AudioContext not unlocked', { reason: 'audio_not_unlocked' });
      onErrorRef.current?.('audio_not_unlocked');
      // Seq-gap repair: this reservation never gets filled.
      skippedSeqsRef.current.add(seq);
      return;
    }
    if (audioCtxRef.current.state === 'suspended') {
      try {
        await audioCtxRef.current.resume();
      } catch (err) {
        const msg = err?.message || 'unknown';
        logVoiceEvent('tts_resume', 'error', `AudioContext.resume() rejected: ${msg}`);
        ttsTimer.fail(`AudioContext resume failed: ${msg}`, { reason: 'audio_suspended' });
        onErrorRef.current?.('audio_suspended', { message: msg });
        skippedSeqsRef.current.add(seq);
        return;
      }
    }
    attachStatechange();

    // TICKET-CODEX-3172406337: re-arm the first-audio mark at the start of each
    // new TTS turn so onFirstAudio fires once per turn, not just the first ever.
    // stop() already resets this ref, but normal turn completion (queue drains
    // naturally without stop()) leaves it true — subsequent turns silently skip
    // the turn-latency telemetry. Re-arm when the queue is idle (no pending, no
    // playing) so only the very first speak() of a new turn resets the flag.
    if (pendingCountRef.current === 0 && !playingRef.current) {
      firstAudioFiredRef.current = false;
    }
    pendingCountRef.current++;
    setIsPending(true);

    // Enqueue an item at this seq and kick playback if it's the head slot
    // and nothing is currently playing. If playback is active, the next
    // onended callback will call playNext and pick up this slot naturally.
    const enqueue = (item) => {
      queueRef.current.set(seq, item);
      if (!playingRef.current && seq === playSeqRef.current) playNext();
    };

    // P6 (2026-06-10): retry-once on transient failures. Returns an object:
    //   { streamed, bytes } after a (possibly best-effort) PCM stream,
    //   { arrayBuffer } on buffered success, { transient: true } on transient
    //   failure (network error or timeout), { deterministic: true } on 4xx,
    //   or { cancelled: true } when stop() fired mid-fetch.
    // A 'transient' result on attempt 0 triggers one retry; on attempt 1 it
    // is treated as final. Deterministic errors never retry. Once STREAMING
    // playback has begun, mid-stream errors are NOT retried (a retry would
    // replay already-heard audio) — we finish with what arrived.
    const attemptFetch = async (attempt) => {
      // T1.1 — AbortController + 25s timeout per attempt. The timeout spans
      // the WHOLE body read (matches the old full-buffer behavior): sentence
      // audio is far shorter than 25s, and a stalled stream must not hang
      // isPending forever.
      const controller = new AbortController();
      inflightControllersRef.current.add(controller);
      const timeoutId = setTimeout(() => {
        try { controller.abort('tts_timeout'); } catch { /* noop */ }
      }, TTS_FETCH_TIMEOUT_MS);

      try {
        const auth = await getAuthHeaders();
        const resp = await fetch(TTS_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth },
          // format:'pcm' asks the server for raw PCM it can stream-play. An
          // older server ignores it and returns MP3; a fallback provider may
          // answer in a non-PCM format — the Content-Type branch below lands
          // those on the buffered path. Fully forward/backward compatible.
          body: JSON.stringify(transport === 'stream'
            ? { action: 'agent.tts_stream', text, format: 'pcm' }
            : { action: 'agent.tts_stream', text }),
          signal: controller.signal,
        });

        if (!resp.ok) {
          // Treat 429 and 5xx as transient — let the retry-once path attempt recovery.
          // True deterministic 4xx (400, 401, 403, 404, 422 …) are not retried.
          const isRetryable = resp.status === 429 || resp.status >= 500;
          if (!isRetryable) {
            const errBody = await resp.json().catch(() => ({}));
            const detail = errBody.error || 'unknown';
            return { deterministic: true, status: resp.status, detail };
          }
          // Retryable — return transient result so the caller's retry-once logic runs.
          // Carry the HTTP status so exhausted-retry callers can surface tts_http_error.
          const errBody = await resp.json().catch(() => ({}));
          const detail = errBody.error || 'unknown';
          return { transient: true, httpStatus: resp.status, detail };
        }

        const contentType = resp.headers.get?.('content-type') || '';

        // ── Streaming PCM branch (TICKET-WISPR-TTS-STREAM) ──────────────
        if (/^audio\/pcm/i.test(contentType) && resp.body?.getReader) {
          const item = {
            kind: 'pcm',
            chunks: [],
            done: false,
            rate: parsePcmRate(contentType),
            player: null,
            bytes: 0,
          };
          const firstChunkTimer = startTimer('tts_first_chunk');
          let firstChunk = true;
          enqueue(item); // playable immediately — chunks stream into it
          const reader = resp.body.getReader();
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              if (cancelledRef.current) {
                reader.cancel().catch(() => { /* spine-emit-ok: cancelling an already-broken reader on user stop, benign */ });
                return { cancelled: true };
              }
              if (!value || value.byteLength === 0) continue;
              item.bytes += value.byteLength;
              if (firstChunk) {
                firstChunk = false;
                // Time-to-first-audio-byte — THE streaming-latency metric.
                firstChunkTimer.ok(`first PCM chunk (seq=${seq})`, { seq, chunk_bytes: value.byteLength });
              }
              if (item.player) item.player.append(value);
              else item.chunks.push(value);
            }
            item.done = true;
            if (item.player) item.player.end();
            return { streamed: true, bytes: item.bytes };
          } catch (err) {
            if (cancelledRef.current) return { cancelled: true };
            // Mid-stream failure: play out what arrived; do NOT retry (would
            // replay already-heard audio). Surface on the spine.
            item.done = true;
            if (item.player) item.player.end();
            const msg = err?.message || String(err);
            logVoiceEvent('tts_stream_interrupted', 'error', `PCM stream broke mid-read: ${msg}`, {
              seq, bytes_received: item.bytes, error_message: msg.slice(0, 200),
            });
            return { streamed: true, bytes: item.bytes, interrupted: true };
          }
        }

        // ── Legacy buffered branch (non-PCM Content-Type) ───────────────
        const arrayBuffer = await resp.arrayBuffer();
        if (cancelledRef.current) return { cancelled: true };
        return { arrayBuffer };
      } catch (err) {
        if (cancelledRef.current) return { cancelled: true };
        const isTimeout = err?.name === 'AbortError' && controller.signal.reason === 'tts_timeout';
        const isUserCancel = err?.name === 'AbortError' && !isTimeout;
        if (isUserCancel) return { cancelled: true };
        // Network error or timeout — transient, eligible for one retry.
        const msg = err?.message || String(err);
        return { transient: true, isTimeout, msg };
      } finally {
        clearTimeout(timeoutId);
        inflightControllersRef.current.delete(controller);
      }
    };

    try {
      let result = await attemptFetch(0);

      // P6: retry once on transient failure (network error or timeout).
      if (result.transient) {
        logVoiceEvent('tts_retry', 'ok', `transient TTS failure — retrying once (seq=${seq})`, {
          seq,
          was_timeout: result.isTimeout,
          first_error: result.msg,
        });
        result = await attemptFetch(1);
      }

      if (result.cancelled) return;

      if (result.deterministic) {
        ttsTimer.fail(`TTS API ${result.status}: ${result.detail}`, { http_status: result.status });
        logVoiceEvent('tts_http_error', 'error', `TTS API ${result.status}: ${result.detail}`, { http_status: result.status });
        console.error('[useStreamingTTS] TTS API error:', result.status, result.detail);
        onErrorRef.current?.('tts_http_error', { status: result.status, detail: result.detail });
        // Seq-gap repair — mark this slot skipped so queue advances past it.
        if (!cancelledRef.current) {
          skippedSeqsRef.current.add(seq);
          if (seq === playSeqRef.current && !playingRef.current) playNext();
        }
        return;
      }

      if (result.transient) {
        // Both attempts exhausted — surface the terminal failure.
        if (result.isTimeout) {
          ttsTimer.fail(`TTS fetch timed out after ${TTS_FETCH_TIMEOUT_MS}ms`, { timeout_ms: TTS_FETCH_TIMEOUT_MS });
          logVoiceEvent('tts_timeout', 'error', `TTS fetch exceeded ${TTS_FETCH_TIMEOUT_MS}ms — aborted`, { timeout_ms: TTS_FETCH_TIMEOUT_MS });
          console.error('[useStreamingTTS] TTS fetch timeout');
          onErrorRef.current?.('tts_timeout', { timeout_ms: TTS_FETCH_TIMEOUT_MS });
        } else if (result.httpStatus) {
          // Retryable HTTP error (5xx/429) that still failed after retry — surface as tts_http_error.
          ttsTimer.fail(`TTS API ${result.httpStatus}: ${result.detail}`, { http_status: result.httpStatus });
          logVoiceEvent('tts_http_error', 'error', `TTS API ${result.httpStatus}: ${result.detail}`, { http_status: result.httpStatus });
          console.error('[useStreamingTTS] TTS API error (after retry):', result.httpStatus, result.detail);
          onErrorRef.current?.('tts_http_error', { status: result.httpStatus, detail: result.detail });
        } else {
          ttsTimer.fail(result.msg);
          logVoiceEvent('tts_fetch_error', 'error', `TTS fetch failed: ${result.msg}`);
          console.error('[useStreamingTTS] fetch error:', result.msg);
          onErrorRef.current?.('tts_fetch_error', { message: result.msg });
        }
        // Seq-gap repair — mark this slot skipped.
        if (!cancelledRef.current) {
          skippedSeqsRef.current.add(seq);
          if (seq === playSeqRef.current && !playingRef.current) playNext();
        }
        return;
      }

      // Success paths.
      if (result.streamed) {
        // Item was enqueued when the stream opened and may already be playing
        // (or even finished) — this row records the completed transfer.
        ttsTimer.ok(`TTS stream: ${(result.bytes / 1024).toFixed(0)}KB (seq=${seq})`, {
          audio_bytes: result.bytes, text_length: text.length, seq,
          transport: 'pcm_stream', interrupted: result.interrupted === true,
        });
        return;
      }
      const { arrayBuffer } = result;
      ttsTimer.ok(`TTS audio: ${(arrayBuffer.byteLength / 1024).toFixed(0)}KB (seq=${seq})`, { audio_bytes: arrayBuffer.byteLength, text_length: text.length, seq });
      enqueue({ kind: 'encoded', arrayBuffer });
    } catch (err) {
      // Unexpected error path — should not be reached with the above logic, but
      // keep a safety net so speak() never propagates an unhandled rejection.
      // spine-emit-ok: rethrow-guard; all reachable error paths are handled above
      const msg = err?.message || String(err);
      logVoiceEvent('tts_fetch_error', 'error', `TTS unexpected error: ${msg}`);
      onErrorRef.current?.('tts_fetch_error', { message: msg });
      if (!cancelledRef.current) {
        skippedSeqsRef.current.add(seq);
        if (seq === playSeqRef.current && !playingRef.current) playNext();
      }
    } finally {
      pendingCountRef.current--;
      if (pendingCountRef.current === 0) setIsPending(false);
    }
  }, [playNext, attachStatechange]);

  // Cleanup statechange listener on unmount
  useEffect(() => {
    return () => {
      // TICKET-073 (karaoke) — never leave a word interval running past unmount.
      clearWordTimer();
      const ctx = audioCtxRef.current;
      const handler = statechangeHandlerRef.current;
      if (ctx && handler && typeof ctx.removeEventListener === 'function') {
        try { ctx.removeEventListener('statechange', handler); } catch { /* noop */ }
      }
      statechangeHandlerRef.current = null;
    };
  }, [clearWordTimer]);

  const pause = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || typeof ctx.suspend !== 'function') return;
    if (ctx.state === 'running') {
      try { ctx.suspend(); } catch { /* noop */ }
    }
  }, []);

  const resume = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || typeof ctx.resume !== 'function') return;
    if (ctx.state === 'suspended') {
      try { ctx.resume(); } catch { /* noop */ }
    }
  }, []);

  // TICKET-103: expose a stable getter for the barge-in VAD's sourceActive signal.
  // Returns true when audio is actively playing on EITHER path: the buffered
  // path's AudioBufferSource (currentSourceRef) or the streaming path's live
  // pcmPlayer. The function identity is stable (no useCallback needed) because
  // it closes over refs — callers pass it to useVoiceBargeIn's getSourceActive
  // so the VAD can gate the rolling floor to actual playback state.
  const isSourceActive = () =>
    currentSourceRef.current !== null
    || (activePcmPlayerRef.current !== null && activePcmPlayerRef.current.isActive());

  // TICKET-623 — the barge-in echo guard's known reference. Returns the
  // active pcmPlayer's recent {rms,t}[] envelope, or [] when nothing is
  // playing or the legacy buffered path is in use (no pcmPlayer instance —
  // e.g. a non-PCM fallback provider). Stable function identity (closes
  // over the ref), same pattern as isSourceActive.
  const getReferenceEnvelope = () => activePcmPlayerRef.current?.getEnvelope() ?? [];

  return {
    speak, stop, pause, resume, isSpeaking, isPending, unlockAudio,
    audioContextRef: audioCtxRef, isSourceActive, getReferenceEnvelope,
  };
}
